import React from "react";
import { useTranslation } from "react-i18next";
import { Member } from "../../features/members/models/Member";
import { VisitOutcome } from "../../features/sessions/models/VisitOutcome";
import { SessionVisits } from "../../features/sessions/visits-slice";
import { formatDateDisplay } from "../../features/sessions/utils/DisplayMappers";

interface CancelledAttendanceProps {
  /** The cancelled occurrence date (YYYY-MM-DD). */
  date: string;
  /** Visits already recorded on the date before it was cancelled. */
  outcomes: SessionVisits;
  members: Member[];
  embedded?: boolean;
}

/**
 * The attendance panel of an instructor-cancelled date: the notice, no record action, and
 * any Visit recorded before the cancel listed read-only.
 */
const CancelledAttendance: React.FC<CancelledAttendanceProps> = ({ date, outcomes, members, embedded = false }) => {
  const { t } = useTranslation();
  const recorded = Object.entries(outcomes).filter(([, outcome]) => outcome !== VisitOutcome.Unmarked);
  const nameOf = (memberId: string): string => {
    const member = members.find((m) => m.id === memberId);
    return member ? `${member.firstName} ${member.lastName}`.trim() : t("sessions.formerMember", "Former member");
  };

  return (
    <div className={embedded ? "py-2" : "with-border rounded-2xl p-4 lg:p-6"}>
      <p className="text-sm font-medium text-grey-10 dark:text-white">{formatDateDisplay(date)}</p>
      <p role="status" className="mt-3 rounded-xl bg-primary-95 p-3 text-sm text-grey-20 dark:bg-primary-20 dark:text-grey-90">
        {t("sessions.attendance.cancelledNotice")}
      </p>
      {recorded.length > 0 && (
        <ul className="mt-4 divide-y divide-grey-90 dark:divide-primary-20">
          {recorded.map(([memberId, outcome]) => (
            <li key={memberId} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className="truncate text-grey-10 dark:text-white">{nameOf(memberId)}</span>
              <span className="text-grey-40 dark:text-grey-60">{t(`sessions.attendance.outcomes.${outcome}`)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CancelledAttendance;
